/**
 * SB6-ANA-CACHE-02 — full-res prefetch priority queue + scheduler.
 *
 * Caching alone (CACHE-01) only helps the *second* visit. To make image→image
 * stepping fluent on a real internet connection, the full-res bytes of the
 * images the user is *about to* open must already be downloading before they
 * get there. This module is the engine that does that.
 *
 * ## Tiers (story §C)
 * - **P0 — active image.** The image the Viewer is showing right now. Always
 *   wins; jumps the queue on every activation.
 * - **P1 — navigation window (automatic).** The next `WINDOW_AHEAD` and previous
 *   `WINDOW_BEHIND` images in the **current filtered order** (the order the user
 *   actually navigates). Re-sorted on every activation and on every filter
 *   change.
 * - **P2 — whole dataset (explicit only).** Enqueued by the "Download dataset"
 *   action: the filtered order first, then any remaining images of the
 *   acquisition. Never automatic on open (Q1 decision).
 *
 * ## Scheduler
 * - At most `MAX_CONCURRENT` downloads in flight, all via CACHE-01's
 *   `fetchThrough` (canonical-key cache-through; a hit short-circuits the
 *   network).
 * - **Active-loading throttle.** While the P0 active image's full-res isn't
 *   cached yet, the lower-priority warm (P1/P2) is held to
 *   `ACTIVE_LOADING_MAX_CONCURRENT` so the warm doesn't saturate the browser's
 *   per-host connection pool and starve the Viewer's own thumb900 request
 *   (which loads outside this queue). The active image itself may still start
 *   up to `MAX_CONCURRENT`; the cap ramps back up the instant it's cached.
 * - **Strict priority.** A pending P0/P1 always starts before any P2. In-flight
 *   P2 fetches are allowed to *complete*, but no NEW P2 starts while any P0/P1
 *   is pending (so navigation never waits behind a whole-dataset warm).
 * - **Skip-cached.** Before fetching, a cheap `imageCache.get` probe skips keys
 *   already in the cache (counts them toward progress without a network hit).
 * - **In-flight dedupe.** A key already downloading is never started twice, even
 *   if it appears in multiple tiers (e.g. P0 == the first P1 entry).
 *
 * ## SAS expiry (story §D / Q7)
 * On a 403 (`ImageFetchError.isSasExpired`), the queue pauses, calls the
 * injected `onSasExpired` re-mint hook **once** (the host re-fetches the image
 * list → fresh SAS batch), swaps the fresh URLs into queued entries, and
 * resumes. If a 403 recurs *after* a successful re-mint, the queue surfaces an
 * error and stops — it never loops.
 *
 * ## Progress
 * A small subscribable store (`subscribe` / `getProgress`) exposes per-dataset
 * `{ cachedCount, totalCount, isDownloading, error }`. `usePrefetchProgress`
 * (a `useSyncExternalStore` hook) is the React surface. The story's progress
 * lives in the PROG-01 statusbar item, which is not built yet — CACHE-02 plugs
 * its progress in via this store and ships a minimal inline affordance next to
 * the Download action in the meantime (see `usePrefetch.ts`).
 *
 * The module is a singleton (one scheduler per app); it is **not** a React
 * concern beyond the thin hooks at the bottom. This keeps re-prioritisation
 * instant (a store update, not a re-render cascade) and lets pop-out windows
 * share one queue against the origin-wide cache.
 */
import {
  fetchThrough,
  get as cacheGet,
  ImageFetchError,
} from "./imageCache.js";
import { canonicalKey } from "./canonicalKey.js";

// ---------------------------------------------------------------------------
// Tunables — all window/concurrency constants live here (story §"in one place").
// ---------------------------------------------------------------------------

/** P1 window: how many images AHEAD of the active one to warm. */
export const WINDOW_AHEAD = 10;
/** P1 window: how many images BEHIND the active one to warm. */
export const WINDOW_BEHIND = 2;
/** Max concurrent `fetchThrough` downloads across all tiers. */
export const MAX_CONCURRENT = 4;
/**
 * Reduced concurrency held while the ACTIVE image's full-res bytes are not yet
 * cached. The Viewer loads the active image's thumb900 directly through OSD's
 * own `<img>` (NOT this queue); if the window/dataset warm saturates the
 * browser's per-host connection pool with 5–8 MB full-res downloads, that
 * thumb900 — and the active full-res itself — stall behind them, which is the
 * "thumbnail never appears, just an error until full-res loads" symptom. So
 * while the user is still waiting on the image they just navigated to, the
 * queue holds back to leave connections free; it ramps back to MAX_CONCURRENT
 * the moment the active image is cached (it's already on screen by then).
 */
export const ACTIVE_LOADING_MAX_CONCURRENT = 2;

/** Priority tiers. Lower number = higher priority. */
export const enum Priority {
  Active = 0,
  Window = 1,
  Dataset = 2,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One queued image, identified by its stable canonical cache key. */
interface QueueEntry {
  /** Stable, origin-less, query-less cache key (survives SAS rotation). */
  key: string;
  /** The (possibly rotating) signed URL to fetch from on a cache miss. */
  url: string;
  /** Current priority tier. An entry can be re-tiered in place. */
  priority: Priority;
}

/**
 * ANA-R2-05 — the terminal snapshot of the most recent EXPLICIT download run.
 *
 * When an explicit "Download all images" run drains (every P2 entry either
 * cached or dropped), the scheduler latches this so the status overlay can
 * show a non-silent terminal state ("Downloaded N images" / "Downloaded n of
 * N — some failed") instead of just vanishing (AC3). It is NOT set for a
 * window-prefetch warm or for a user-cancelled run (a cancel is the user's own
 * action; the overlay disappearing is the expected result). Cleared by
 * `acknowledgeRun()` once the overlay has shown it (auto-hide).
 */
export interface DownloadRunResult {
  /** Images this run set out to fetch (already-cached ones excluded). */
  total: number;
  /** How many of those completed (cached) by the time the run drained. */
  completed: number;
  /** How many were dropped due to a non-recoverable fetch error. */
  failed: number;
}

export interface PrefetchProgress {
  /** The acquisition this progress describes (null = idle/unset). */
  dataAcquisitionId: string | null;
  /** Images of the dataset whose full-res bytes are in the cache. */
  cachedCount: number;
  /** Total images in the dataset (denominator of the readout). */
  totalCount: number;
  /** True while a whole-dataset (P2) download is active or pending. */
  isDownloading: boolean;
  /** Set when a re-minted SAS still 403s — the queue has stopped. */
  error: string | null;
  /**
   * ANA-R2-05 — terminal result of the last explicit download run, latched
   * when the run drains so the overlay can show a terminal state. Null when no
   * run has finished (or the overlay has acknowledged it).
   */
  lastRun: DownloadRunResult | null;
}

/**
 * The host wiring the queue needs. `onSasExpired` re-fetches the image list
 * (re-minting all SAS URLs) and resolves to a fresh `imageId → fullUrl` map;
 * the scheduler swaps those URLs into its queued entries. Rejecting (or
 * resolving empty) is treated as a failed re-mint → error state.
 */
export interface PrefetchHostHooks {
  onSasExpired: () => Promise<Map<string, string>>;
}

/** A single image as the queue understands it. */
export interface PrefetchImage {
  imageId: string;
  /** Full-res signed URL. Empty/falsey entries are skipped. */
  fullUrl: string;
}

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

class PrefetchScheduler {
  /** All entries we intend to fetch, keyed by canonical key (dedupe). */
  private entries = new Map<string, QueueEntry>();
  /** Canonical keys currently being fetched (in-flight dedupe + cap). */
  private inFlight = new Set<string>();
  /** Canonical keys confirmed in the cache this session (skip-cached). */
  private cached = new Set<string>();
  /** imageId → fullUrl for the current acquisition (for re-mint swaps). */
  private hostHooks: PrefetchHostHooks | null = null;

  /**
   * Canonical key of the current P0 active image, or null. Drives the dynamic
   * concurrency throttle: while this key is set and NOT yet cached, the
   * scheduler runs at `ACTIVE_LOADING_MAX_CONCURRENT` so the browser keeps
   * connections free for the Viewer's (queue-external) thumb900 load.
   */
  private activeKey: string | null = null;

  /** Re-mint guard: true while a SAS list re-fetch is in flight. */
  private remintInFlight = false;
  /**
   * How many successful re-mints have happened this download run. The story's
   * rule: re-mint ONCE on 403; if a 403 recurs *after* a successful re-mint,
   * surface an error and stop (never loop). So `remintDone >= 1` + a fresh 403
   * = exhausted.
   */
  private remintDone = 0;
  /** Latched once we've given up (a 403 recurred after a re-mint). */
  private remintExhausted = false;

  /** Progress snapshot + subscribers (zustand-free external store). */
  private progress: PrefetchProgress = {
    dataAcquisitionId: null,
    cachedCount: 0,
    totalCount: 0,
    isDownloading: false,
    error: null,
    lastRun: null,
  };
  private listeners = new Set<() => void>();

  // -- explicit-download-run accounting (ANA-R2-05) --------------------------
  /** True while we're inside an explicit download run (not a window warm). */
  private runActive = false;
  /** cachedCount at the moment the current run was started. */
  private runStartCached = 0;
  /** Images this run set out to fetch (already-cached ones excluded). */
  private runTarget = 0;
  /** Images dropped this run due to a non-recoverable fetch error. */
  private runFailed = 0;

  // -- public store surface --------------------------------------------------

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  getProgress = (): PrefetchProgress => this.progress;

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private setProgress(patch: Partial<PrefetchProgress>): void {
    this.progress = { ...this.progress, ...patch };
    this.emit();
  }

  // -- configuration ---------------------------------------------------------

  setHostHooks(hooks: PrefetchHostHooks | null): void {
    this.hostHooks = hooks;
  }

  /**
   * Bind the scheduler to an acquisition. Switching acquisitions resets the
   * dataset-scoped state (entries, error, re-mint guards) but NOT the cache —
   * the origin-wide Cache Storage is shared across acquisitions.
   */
  setDataset(dataAcquisitionId: string, totalCount: number): void {
    if (this.progress.dataAcquisitionId === dataAcquisitionId) {
      // Same dataset — only the total may have grown (list page settled).
      if (this.progress.totalCount !== totalCount) {
        this.setProgress({ totalCount });
      }
      return;
    }
    this.entries.clear();
    // in-flight fetches from the previous dataset finish harmlessly; their
    // keys are simply no longer in `entries`, so completions are ignored.
    this.cached.clear();
    this.activeKey = null;
    this.remintInFlight = false;
    this.remintDone = 0;
    this.remintExhausted = false;
    this.runActive = false;
    this.runTarget = 0;
    this.runFailed = 0;
    this.runStartCached = 0;
    this.setProgress({
      dataAcquisitionId,
      cachedCount: 0,
      totalCount,
      isDownloading: false,
      error: null,
      lastRun: null,
    });
  }

  // -- enqueue paths ---------------------------------------------------------

  /**
   * Set the P0 active image and the P1 window in ONE atomic update, derived
   * from the current filtered order. Re-prioritises instantly: the new active
   * image + window are (re-)tiered to P0/P1; images that fall out of the window
   * drop to P2 if a dataset download is running, else are removed.
   *
   * `windowImages` MUST be the filtered-order slice the host computed
   * (active first, then ahead/behind) — the scheduler trusts the order.
   */
  setActiveAndWindow(
    active: PrefetchImage | null,
    windowImages: PrefetchImage[],
  ): void {
    const datasetDownloadActive = this.progress.isDownloading;

    // Demote every current P0/P1 entry: keep it only if a dataset download is
    // running (→ P2), otherwise drop it (we'll re-add the new window below).
    for (const [key, entry] of [...this.entries]) {
      if (entry.priority === Priority.Dataset) continue;
      if (datasetDownloadActive) {
        entry.priority = Priority.Dataset;
      } else {
        this.entries.delete(key);
      }
    }

    // Track the active image's key for the dynamic concurrency throttle. A
    // cached active needs no throttle (it's already displayable); an uncached
    // one holds the queue back until its bytes land.
    this.activeKey = active?.fullUrl ? canonicalKey(active.fullUrl) : null;

    if (active?.fullUrl) {
      this.upsert(active, Priority.Active);
    }
    for (const img of windowImages) {
      if (!img.fullUrl) continue;
      const key = canonicalKey(img.fullUrl);
      // Don't downgrade the active image to window priority.
      if (img.imageId === active?.imageId) continue;
      this.upsert(img, Priority.Window, key);
    }

    this.pump();
  }

  /**
   * Explicit "Download dataset": enqueue EVERY image at P2 (filtered order
   * first, then the rest — the host passes them already concatenated in that
   * order). Sets `isDownloading`. Existing P0/P1 entries keep their tier.
   */
  enqueueDataset(images: PrefetchImage[]): void {
    this.remintDone = 0;
    this.remintExhausted = false;
    // ANA-R2-05 — open a fresh download run for terminal-state accounting. The
    // target is the number of NOT-already-cached images we enqueue this run;
    // `runFailed` counts non-recoverable drops; `runStartCached` is the
    // baseline so the terminal "completed" count reflects only THIS run.
    this.runActive = true;
    this.runFailed = 0;
    this.runStartCached = this.progress.cachedCount;
    let target = 0;
    this.setProgress({ isDownloading: true, error: null, lastRun: null });
    for (const img of images) {
      if (!img.fullUrl) continue;
      const key = canonicalKey(img.fullUrl);
      if (this.cached.has(key)) continue; // already cached this session
      if (this.entries.has(key)) continue; // keep its (possibly higher) tier
      this.entries.set(key, {
        key,
        url: img.fullUrl,
        priority: Priority.Dataset,
      });
      target += 1;
    }
    this.runTarget = target;
    this.recountCachedAsync(images);
    this.pump();
  }

  /** Cancel an in-progress dataset download: drop pending P2 entries. */
  cancelDataset(): void {
    for (const [key, entry] of [...this.entries]) {
      if (entry.priority === Priority.Dataset && !this.inFlight.has(key)) {
        this.entries.delete(key);
      }
    }
    // ANA-R2-05 — a cancel is the user's own action; close the run WITHOUT
    // latching a terminal `lastRun` (the overlay disappearing is the expected
    // result of pressing Cancel, not a silent disappearance).
    this.runActive = false;
    this.setProgress({ isDownloading: false, lastRun: null });
    // In-flight P2 fetches are allowed to finish (cheap, already paid for);
    // their completion just bumps cachedCount.
  }

  /**
   * ANA-R2-05 — the overlay calls this once it has shown the terminal state so
   * it can auto-hide. Clears the latched `lastRun` without touching anything
   * else.
   */
  acknowledgeRun(): void {
    if (this.progress.lastRun) this.setProgress({ lastRun: null });
  }

  // -- internal --------------------------------------------------------------

  private upsert(img: PrefetchImage, priority: Priority, key?: string): void {
    const k = key ?? canonicalKey(img.fullUrl);
    const existing = this.entries.get(k);
    if (existing) {
      // Re-tier in place; always keep the HIGHEST priority (lowest number) and
      // refresh the URL (a re-mint may have rotated it).
      existing.url = img.fullUrl;
      if (priority < existing.priority) existing.priority = priority;
      return;
    }
    this.entries.set(k, { key: k, url: img.fullUrl, priority });
  }

  /**
   * Probe the cache for a set of images and fold already-cached ones into the
   * `cached` set + progress count (skip-cached / resumable downloads). Async,
   * best-effort; never blocks the pump.
   */
  private recountCachedAsync(images: PrefetchImage[]): void {
    void (async () => {
      let added = 0;
      for (const img of images) {
        if (!img.fullUrl) continue;
        const key = canonicalKey(img.fullUrl);
        if (this.cached.has(key)) continue;
        const hit = await cacheGet(key);
        if (hit) {
          this.cached.add(key);
          this.entries.delete(key); // already cached → nothing to fetch
          added += 1;
        }
      }
      if (added > 0) {
        this.setProgress({
          cachedCount: this.progress.cachedCount + added,
        });
        this.pump();
      }
    })();
  }

  /**
   * Pick the next entry to start, honouring strict priority + the
   * "no new P2 while P0/P1 pending" rule. Returns null when nothing is eligible.
   */
  private nextEligible(): QueueEntry | null {
    let best: QueueEntry | null = null;
    let pendingHighPriority = false;
    for (const entry of this.entries.values()) {
      if (this.inFlight.has(entry.key)) continue;
      if (entry.priority < Priority.Dataset) pendingHighPriority = true;
      if (!best || entry.priority < best.priority) best = entry;
    }
    if (!best) return null;
    // Strict gate: never START a P2 while any P0/P1 is still pending. (We can't
    // reach here with best === P2 AND a pending high-priority entry unless the
    // high-priority one is in flight — but the loop already accounts for that.)
    if (best.priority === Priority.Dataset && pendingHighPriority) return null;
    return best;
  }

  /**
   * Current concurrency cap. Throttled to `ACTIVE_LOADING_MAX_CONCURRENT` while
   * the active image's full-res isn't cached yet (keep connections free for the
   * Viewer's own thumb900 load); otherwise the full `MAX_CONCURRENT`.
   */
  private effectiveConcurrency(): number {
    if (this.activeKey !== null && !this.cached.has(this.activeKey)) {
      return ACTIVE_LOADING_MAX_CONCURRENT;
    }
    return MAX_CONCURRENT;
  }

  /** Start as many eligible fetches as the concurrency cap allows. */
  private pump(): void {
    if (this.remintInFlight || this.remintExhausted) return;
    // The throttled cap restrains the lower-priority WARM (P1/P2); the active
    // image (P0) may always start up to the hard `MAX_CONCURRENT` ceiling so a
    // navigation is never made to wait behind stale window downloads still in
    // flight from the previous image.
    const cap = this.effectiveConcurrency();
    while (this.inFlight.size < MAX_CONCURRENT) {
      const entry = this.nextEligible();
      if (!entry) break;
      if (entry.priority !== Priority.Active && this.inFlight.size >= cap)
        break;
      this.startFetch(entry);
    }
    this.reconcileDownloadingFlag();
  }

  /** Flip `isDownloading` off once no P2 work remains (pending or in flight). */
  private reconcileDownloadingFlag(): void {
    if (!this.progress.isDownloading) return;
    const anyDatasetLeft = [...this.entries.values()].some(
      (e) => e.priority === Priority.Dataset,
    );
    if (anyDatasetLeft) return;
    // ANA-R2-05 — the explicit run drained. Latch a terminal result so the
    // overlay shows "Downloaded N images" / "Downloaded n of N — some failed"
    // instead of vanishing silently (AC3). `completed` is THIS run's net
    // cache gain; `total` is the run target.
    const lastRun: DownloadRunResult | null = this.runActive
      ? {
          total: this.runTarget,
          completed: Math.max(
            0,
            this.progress.cachedCount - this.runStartCached,
          ),
          failed: this.runFailed,
        }
      : null;
    this.runActive = false;
    this.setProgress({ isDownloading: false, lastRun });
  }

  private startFetch(entry: QueueEntry): void {
    this.inFlight.add(entry.key);
    void (async () => {
      try {
        // Skip-cached: a cheap probe. `fetchThrough` also checks the cache, but
        // probing here lets us count the hit toward progress without a fetch.
        const hit = await cacheGet(entry.key);
        if (hit) {
          this.onCached(entry.key);
          return;
        }
        await fetchThrough(entry.url);
        this.onCached(entry.key);
      } catch (err) {
        this.inFlight.delete(entry.key);
        if (err instanceof ImageFetchError && err.isSasExpired) {
          this.handleSasExpiry(entry);
          return;
        }
        // Non-403 (network blip, 500, …): drop this entry, keep going. A
        // resumable re-trigger will re-enqueue it; we don't want one bad image
        // to wedge the whole queue. ANA-R2-05 — count a dropped P2 image as a
        // run failure so the terminal state can say "some failed".
        if (this.runActive && entry.priority === Priority.Dataset) {
          this.runFailed += 1;
        }
        this.entries.delete(entry.key);
        this.pump();
      }
    })();
  }

  private onCached(key: string): void {
    this.inFlight.delete(key);
    if (this.entries.has(key)) {
      this.entries.delete(key);
      if (!this.cached.has(key)) {
        this.cached.add(key);
        this.setProgress({ cachedCount: this.progress.cachedCount + 1 });
      }
    }
    this.pump();
  }

  /**
   * SAS expiry: pause, re-mint the list ONCE, swap fresh URLs into queued
   * entries, resume. The faulting entry is re-queued (its key stays in
   * `entries`) so it retries with the fresh URL.
   */
  private handleSasExpiry(faulted: QueueEntry): void {
    // Ensure the faulted entry is still queued so it retries post-remint.
    if (!this.entries.has(faulted.key)) {
      this.entries.set(faulted.key, { ...faulted });
    }
    // A 403 AFTER a successful re-mint = the fresh batch is already expired or
    // wrong. Stop — never loop (story §D).
    if (this.remintExhausted || this.remintDone >= 1) {
      this.remintExhausted = true;
      this.fail("Image links expired and could not be refreshed.");
      return;
    }
    if (this.remintInFlight) return; // a re-mint is already running; wait for it
    const hooks = this.hostHooks;
    if (!hooks) {
      this.fail("Image links expired (no refresh available).");
      return;
    }

    this.remintInFlight = true;
    void (async () => {
      try {
        const fresh = await hooks.onSasExpired();
        // Swap fresh URLs into queued entries by canonical key. A re-minted URL
        // maps to the SAME canonical key, so we match on the key.
        const freshByKey = new Map<string, string>();
        for (const url of fresh.values()) {
          if (url) freshByKey.set(canonicalKey(url), url);
        }
        let swapped = 0;
        for (const entry of this.entries.values()) {
          const next = freshByKey.get(entry.key);
          if (next) {
            entry.url = next;
            swapped += 1;
          }
        }
        this.remintInFlight = false;
        if (swapped === 0) {
          // The re-mint produced nothing usable — treat as exhausted.
          this.remintExhausted = true;
          this.fail("Image links expired and could not be refreshed.");
          return;
        }
        this.remintDone += 1;
        this.pump();
      } catch {
        this.remintInFlight = false;
        this.remintExhausted = true;
        this.fail("Image links expired and could not be refreshed.");
      }
    })();
  }

  private fail(message: string): void {
    // ANA-R2-05 — a hard stop (SAS could not be refreshed). Close the run; the
    // overlay surfaces `error` rather than a `lastRun` terminal.
    this.runActive = false;
    this.setProgress({ error: message, isDownloading: false, lastRun: null });
  }

  // -- cross-module dedupe ---------------------------------------------------

  /**
   * Is this signed URL's canonical key currently being fetched by the queue (or
   * already known cached this session)? `useCachedImageUrl` consults this before
   * its own background cache-populate so the active image isn't downloaded twice
   * (the queue owns P0). Cheap synchronous set lookups — no `await`.
   */
  isHandling(signedUrl: string): boolean {
    const key = canonicalKey(signedUrl);
    return this.inFlight.has(key) || this.cached.has(key);
  }

  // -- test/diagnostic surface ----------------------------------------------

  /** @internal — current pending entries (test introspection). */
  _debugEntries(): { key: string; priority: Priority; url: string }[] {
    return [...this.entries.values()].map((e) => ({ ...e }));
  }

  /** @internal — keys currently in flight (test introspection). */
  _debugInFlight(): string[] {
    return [...this.inFlight];
  }

  /** @internal — reset everything (tests run against a clean singleton). */
  _reset(): void {
    this.entries.clear();
    this.inFlight.clear();
    this.cached.clear();
    this.activeKey = null;
    this.hostHooks = null;
    this.remintInFlight = false;
    this.remintDone = 0;
    this.remintExhausted = false;
    this.runActive = false;
    this.runTarget = 0;
    this.runFailed = 0;
    this.runStartCached = 0;
    this.progress = {
      dataAcquisitionId: null,
      cachedCount: 0,
      totalCount: 0,
      isDownloading: false,
      error: null,
      lastRun: null,
    };
  }
}

/** The app-wide singleton scheduler. */
export const prefetchScheduler = new PrefetchScheduler();
